import React, { useState, useEffect } from "react";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { formatDate } from "../../requests/utilities/helpers";
import PrintShell, {
  SettingsGroup,
  OptionToggle,
  MultiSelect,
  SegmentedControl,
} from "./print/PrintShell";
import WeeklyScheduleSheet, { COLOR_MODES } from "./print/WeeklyScheduleSheet";

const date_options = { weekday: "short", month: "short", day: "numeric" };

export default function PrintMode(props) {
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showUnavailability, setShowUnavailability] = useState(true);
  const [showRole, setShowRole] = useState(true);
  const [colorMode, setColorMode] = useState("time");
  // Store setting "Includes hours when printing schedules" is the default.
  const [showIndividualHours, setShowIndividualHours] = useState(
    !!props.printHoursShownEnabled
  );
  const [showTotalHours, setShowTotalHours] = useState(
    !!props.printHoursShownEnabled
  );
  const [showBreaks, setShowBreaks] = useState(false);
  const [showOpenShifts, setShowOpenShifts] = useState(true);
  const [showNotes, setShowNotes] = useState(true);

  // Seed the role filter from the schedule's own filter and keep it in sync.
  useEffect(() => {
    if (!props.selectedRolesForFiltering) return;
    if (props.selectedRolesForFiltering.length === 0) {
      setSelectedRoles([]);
    } else if (props.role_names) {
      setSelectedRoles(
        props.selectedRolesForFiltering
          .map((id) => props.role_names.find((r) => r.id === id)?.role_name)
          .filter(Boolean)
      );
    }
  }, [props.selectedRolesForFiltering, props.role_names]);

  const dow_nums = [0, 1, 2, 3, 4, 5, 6];
  const day_names = dow_nums.map((dow) =>
    props.addDays(props.week_start_date, dow).toLocaleDateString("en-us", date_options)
  );
  const weekLabel = `${day_names[0]} – ${day_names[6]}`;
  const storeName = props.storeNames?.find(
    (s) => s.id === Number(props.store_id)
  )?.store_name;

  const baseEmployees =
    props.store_team_member_weekly_info || props.store_team_member_weekly_info_view || [];
  // The view list can carry role-header pseudo rows when segmented by role.
  // Per-day shift arrays are rebuilt from the saved schedule: the schedule
  // page swaps them for Copilot suggestions while Copilot is on, and the
  // printout must always be the saved schedule.
  const employees = baseEmployees
    .filter((e) => !e.isRoleHeader)
    .map((e) => {
      const row = { ...e };
      dow_nums.forEach((dow) => {
        row[dow] = props.filter_df_to_day(
          props.actual_schedule || [],
          "date",
          formatDate(props.addDays(props.week_start_date, dow))
        ).filter((s) => s.store_team_member === e.id);
      });
      return row;
    });

  // Rows filter by primary role, so every roster role must be selectable,
  // plus any role that only appears on shifts (open shifts, cross-training).
  const roleOptions = [
    ...new Set([
      ...employees.map((e) => e.role),
      ...(props.actual_schedule || []).map((s) => s.role_name),
    ]),
  ]
    .filter(Boolean)
    .sort()
    .map((r) => ({ value: r, label: r }));

  const filteredEmployees = selectedRoles.length
    ? employees.filter((e) => selectedRoles.includes(e.role))
    : employees;

  // Open shifts follow the same role filter as the rows.
  const openShifts = (props.actual_schedule || []).filter(
    (s) =>
      s.status === "AVAILABLE" &&
      (!selectedRoles.length || selectedRoles.includes(s.role_name))
  );
  const openShiftsByDay = dow_nums.map((dow) =>
    props.filter_df_to_day(
      openShifts,
      "date",
      formatDate(props.addDays(props.week_start_date, dow))
    )
  );

  const settings = (
    <>
      <SettingsGroup title="Roles">
        <MultiSelect
          value={selectedRoles}
          onChange={setSelectedRoles}
          options={roleOptions}
          placeholder="All roles"
        />
      </SettingsGroup>

      <SettingsGroup title="Hours">
        <OptionToggle
          label="Hours per person"
          checked={showIndividualHours}
          onChange={setShowIndividualHours}
        />
        <OptionToggle
          label="Daily and weekly totals"
          checked={showTotalHours}
          onChange={setShowTotalHours}
        />
      </SettingsGroup>

      <SettingsGroup title="Color shifts by">
        <SegmentedControl
          value={colorMode}
          onChange={setColorMode}
          options={COLOR_MODES}
        />
      </SettingsGroup>

      <SettingsGroup title="Include">
        <OptionToggle
          label="Open shifts"
          hint={openShifts.length ? `${openShifts.length} this week` : "None this week"}
          checked={showOpenShifts}
          onChange={setShowOpenShifts}
          disabled={!openShifts.length}
        />
        <OptionToggle
          label="Main role under name"
          checked={showRole}
          onChange={setShowRole}
        />
        <OptionToggle
          label="Unavailability and time off"
          checked={showUnavailability}
          onChange={setShowUnavailability}
        />
        <OptionToggle
          label="Break times"
          checked={showBreaks}
          onChange={setShowBreaks}
        />
        <OptionToggle
          label="Notes"
          hint={props.notes?.length ? `${props.notes.length} this week` : "None this week"}
          checked={showNotes}
          onChange={setShowNotes}
          disabled={!props.notes?.length}
        />
      </SettingsGroup>
    </>
  );

  const settingsFooter = (
    <button
      type="button"
      onClick={() => props.setShowAdvancedPrintMode(true)}
      className="w-full flex items-center gap-2.5 text-left rounded-lg border border-slate-200 px-3 py-2.5 hover:border-violet-300 hover:bg-violet-50 group"
    >
      <StorefrontIcon sx={{ fontSize: 18 }} className="text-slate-400 group-hover:text-violet-700" />
      <span className="flex-1">
        <span className="block text-[13px] font-medium text-slate-800">
          Print multiple locations
        </span>
        <span className="block text-[11px] text-slate-500">
          One schedule per location, each on its own page
        </span>
      </span>
      <ChevronRightIcon sx={{ fontSize: 18 }} className="text-slate-300 group-hover:text-violet-700" />
    </button>
  );

  return (
    <PrintShell
      title="Print schedule"
      subtitle={weekLabel}
      documentTitle={`Schedule ${weekLabel}`}
      onClose={props.onClose}
      modeSwitcher={props.modeSwitcher}
      loading={props.scheduleLoading}
      settings={settings}
      settingsFooter={settingsFooter}
    >
      <WeeklyScheduleSheet
        title={storeName || "Weekly schedule"}
        subtitle={weekLabel}
        dayNames={day_names}
        employees={filteredEmployees}
        openShiftsByDay={openShiftsByDay}
        subtractOtherStoreHours
        notes={props.notes}
        roleNames={props.role_names}
        options={{
          showHours: showIndividualHours,
          showTotals: showTotalHours,
          colorMode,
          showUnavailability,
          showRole,
          showBreaks,
          showOpenShifts,
          showNotes,
        }}
      />
    </PrintShell>
  );
}
